import axios from "axios";

export interface Priority {
    id: string,
    name: string,
    criteria: string,
    level: number // Higher = higher priority
}

export const PRIORITIES: Priority[] = [
    { id: 'CRITICAL', name: 'Critical', criteria: 'Severe issue that poses an immediate risk of death or serious injury, and must be immediately resolved', level: 4 },
    { id: 'HIGH', name: 'High', criteria: 'Serious issue that poses a risk of injury of staff or customers', level: 3 },
    { id: 'MEDIUM', name: 'Medium', criteria: 'Moderate issue that carries a minor risk of harm, such as slipping hazards, or seriously impacts customer wellbeing and experience', level: 2 },
    { id: 'LOW', name: 'Low', criteria: 'Minor issue that does not pose a risk of harm, but impacts customer experience, wellbeing, or happiness', level: 1 }
]

export interface Category {
    id: string,
    name: string,
    description: string
}

export const CATEGORIES: Category[] = [
    { id: 'CLEANING', name: 'Cleaning', description: 'Issues related to cleaning, site hygiene, spills, window cleaning, clutter, and general cleanliness' },
    { id: 'SECURITY', name: 'Security', description: 'Issues related to security, CCTV, security presence, access control systems, and other security related issues' },
    { id: 'WASTE_MANAGEMENT,', name: 'Waste Management', description: 'Issues related to waste management, waste collection, waste disposal, and other waste related issues' },
    { id: 'MECHANICAL_ELECTRICAL', name: 'Mechanical & Electrical', description: 'Issues related to mechanical and electrical systems, such as HVAC, elevators, escalators, and more' },
    { id: 'PLUMBING', name: 'Plumbing', description: 'Issues related to plumbing, water supply, drainage, and other plumbing related issues' },
    { id: 'PEST_CONTROL', name: 'Pest Control', description: 'Issues related to pest control, pest management, and other pest related issues' },
    { id: 'GROUNDS_MAINTENANCE', name: 'Grounds Maintenance', description: 'Issues related to grounds maintenance, landscaping, and other grounds related issues' },
    { id: 'IT', name: 'IT', description: 'Issues related to IT, networking, software, and other IT related issues' },
    { id: 'FEEDBACK', name: 'Feedback', description: 'Customer and staff feedback' },
    { id: 'OTHER', name: 'Other', description: 'Other issues' },
]

export enum IssueStatus {
    OPEN = 'OPEN',
    RESOLVED = 'RESOLVED'
}

export interface Photo {
    id: string,
    fileKey: string,
    description: string
}

export interface Issue {
    id: string,
    siteId: string,
    title: string,
    description: string,
    location: string,
    photos: Photo[],
    priority: Priority,
    category: Category,
    status: IssueStatus,
    createdAt: number, // Epoch milliseconds
    closedAt?: number // Epoch milliseconds, optional if not resolved
}

export interface CreateIssueCommand {
    title: string,
    description: string,
    location: string,
    photoFileKeys: string[]
}

export const addIssue = async (siteId: string, command: CreateIssueCommand): Promise<Issue> => {
    const res = await axios.post(`/api/public/site/${siteId}/issues`, command);
    return res.data;
}

export const getOpenIssues = async (siteId: string): Promise<Issue[]> => {
    const res = await axios.get(`/api/site/${siteId}/issues/open`);
    return res.data;
}

export const getResolvedIssues = async (siteId: string): Promise<Issue[]> => {
    const res = await axios.get(`/api/site/${siteId}/issues/resolved`);
    return res.data;
}

export const resolveIssue = async (siteId: string, issueId: string): Promise<Issue> => {
    const res = await axios.post(`/api/site/${siteId}/issues/${issueId}/resolve`);
    return res.data;
}

export const getIssueById = async (siteId: string, issueId: string): Promise<Issue> => {
    const res = await axios.get(`/api/site/${siteId}/issues/${issueId}`);
    return res.data;
}

export const getIssuePhotoSrc = (issueId: string, photoId: string): string => {
    return `/api/public/issues/${issueId}/photos/${photoId}`;
}